import { useEffect, useRef, useCallback } from 'react';

const useFocusTrap = (isActive, containerRef, previousElement = null) => {
  const storedPreviousElement = useRef(null);

  // Memoize the focusable elements selector and getter
  const getFocusableElements = useCallback(() => {
    if (!containerRef?.current) return [];

    const focusableSelectors = [
      'button:not([disabled]):not([aria-hidden="true"])',
      'input:not([disabled]):not([aria-hidden="true"])',
      'select:not([disabled]):not([aria-hidden="true"])',
      'textarea:not([disabled]):not([aria-hidden="true"])',
      'a[href]:not([aria-hidden="true"])',
      '[tabindex]:not([tabindex="-1"]):not([aria-hidden="true"])',
      '[contenteditable="true"]:not([aria-hidden="true"])',
      'details:not([aria-hidden="true"])',
      'summary:not([disabled]):not([aria-hidden="true"])',
    ];

    return Array.from(containerRef.current.querySelectorAll(focusableSelectors.join(', '))).filter(
      el => {
        // Additional check for visibility and interactability
        const style = window.getComputedStyle(el);
        return (
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          style.opacity !== '0' &&
          el.offsetWidth > 0 &&
          el.offsetHeight > 0
        );
      }
    );
  }, [containerRef]);

  // Memoize the keydown handler
  const handleKeyDown = useCallback(
    event => {
      if (event.key !== 'Tab') return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey) {
        // Shift + Tab: move backwards
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else if (document.activeElement === lastElement) {
        // Tab: move forwards
        event.preventDefault();
        firstElement.focus();
      }
    },
    [getFocusableElements]
  );

  useEffect(() => {
    if (!isActive || !containerRef?.current) {
      return;
    }

    // Store the element to return focus to (either passed or currently focused)
    storedPreviousElement.current = previousElement || document.activeElement;

    const focusableElements = getFocusableElements();
    if (focusableElements.length === 0) {
      console.warn('No focusable elements found in focus trap container');
      return;
    }

    // Focus the first focusable element
    focusableElements[0].focus();

    // Add event listener
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup function
    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('keydown', handleKeyDown);

      // Restore focus to the previously focused element when the trap is deactivated
      const elementToFocus = storedPreviousElement.current;
      if (elementToFocus && typeof elementToFocus.focus === 'function') {
        // Use setTimeout to ensure the element is still in the DOM
        setTimeout(() => {
          try {
            elementToFocus.focus();
          } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('Failed to restore focus to previous element:', error);
          }
        }, 0);
      }
    };
  }, [isActive, containerRef, previousElement, getFocusableElements, handleKeyDown]);

  // Return a method to manually update the stored previous element
  const updatePreviousElement = useCallback(element => {
    storedPreviousElement.current = element;
  }, []);

  return { updatePreviousElement };
};

export default useFocusTrap;
